import { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../atoms/Button';
import { Icon } from '../atoms/Icon';
import { FormField } from '../molecules/FormField';
import { SearchField } from '../molecules/SearchField';
import { Dialog } from './Dialog';
import styles from './MultiSelect.module.css';

export interface MultiSelectOption {
  id: string;
  label: string;
  /** Group, e.g. the location of a colony. Options keep their order within a group. */
  group?: { id: string; label: string };
}

export interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Text in the row, e.g. "Berta, Frieda" or "12 Völker". */
  summary: string;
  /** Title of the sheet, e.g. "Völker wählen". */
  title: string;
  /** Group shown first, e.g. the location chosen elsewhere in the form. */
  firstGroup?: string | undefined;
  required?: boolean;
  error?: string | undefined;
  hint?: string | undefined;
  /** Shown in the sheet when there are no options. */
  empty: string;
  /** Texts of the design system's own controls (i18n). */
  labels: {
    search: string;
    clearSearch: string;
    noMatch: string;
    /** "{{count}} von {{total}}" for a group. */
    countOf: (count: number, total: number) => string;
    /** Announced in the sheet, e.g. "3 ausgewählt". */
    selected: (count: number) => string;
    selectAll: string;
    selectNone: string;
    done: string;
    cancel: string;
    close: string;
  };
}

/** From this many options on, the sheet offers a search. */
const SEARCH_FROM = 10;

/**
 * Choose several entries from a long list (docs/ux-richtlinien.md §6.7): the
 * form shows one compact row with a summary; tapping it opens a sheet with
 * search, groups with "Alle auswählen" and check marks like an iOS list.
 * "Fertig" applies the selection, cancelling keeps the previous one.
 */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  summary,
  title,
  firstGroup,
  required = false,
  error,
  hint,
  empty,
  labels,
}: MultiSelectProps) {
  const [draft, setDraft] = useState<string[] | null>(null);
  const [query, setQuery] = useState('');
  const open = draft !== null;
  const statusId = useId();

  const close = () => {
    setDraft(null);
    setQuery('');
  };
  const done = () => {
    if (draft) onChange(options.filter((o) => draft.includes(o.id)).map((o) => o.id));
    close();
  };

  const chosen = new Set(draft ?? []);
  const toggle = (ids: string[], on: boolean) => {
    const next = new Set(chosen);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    setDraft(options.filter((o) => next.has(o.id)).map((o) => o.id));
  };

  const needle = query.trim().toLocaleLowerCase('de');
  const visible = needle
    ? options.filter((o) => o.label.toLocaleLowerCase('de').includes(needle))
    : options;
  const groups = new Map<string, { label: string; options: MultiSelectOption[] }>();
  for (const o of visible) {
    const key = o.group?.id ?? '';
    const g = groups.get(key) ?? { label: o.group?.label ?? '', options: [] };
    g.options.push(o);
    groups.set(key, g);
  }
  const ordered = [...groups].sort(
    ([a], [b]) => Number(b === firstGroup) - Number(a === firstGroup),
  );
  const total = (key: string) => options.filter((o) => (o.group?.id ?? '') === key);

  return (
    <>
      <FormField label={label} required={required} error={error} hint={hint}>
        {({ invalid, ...p }) => (
          <button
            type="button"
            {...p}
            className={[styles.row, invalid && styles.invalid].filter(Boolean).join(' ')}
            aria-haspopup="dialog"
            onClick={() => setDraft(selected)}
          >
            <span className={styles.summary}>{summary}</span>
            <Icon name="chevron-right" size="sm" className={styles.chevron} />
          </button>
        )}
      </FormField>
      {open &&
        createPortal(
          <Dialog
            open
            onClose={close}
            title={title}
            closeLabel={labels.close}
            description={
              <span id={statusId} role="status">
                {labels.selected(chosen.size)}
              </span>
            }
            actions={
              <>
                <Button variant="secondary" onClick={close}>
                  {labels.cancel}
                </Button>
                <Button onClick={done}>{labels.done}</Button>
              </>
            }
          >
            {options.length >= SEARCH_FROM && (
              <SearchField
                value={query}
                onValueChange={setQuery}
                clearLabel={labels.clearSearch}
                placeholder={labels.search}
                aria-label={labels.search}
              />
            )}
            {options.length === 0 && <p className={styles.note}>{empty}</p>}
            {options.length > 0 && visible.length === 0 && (
              <p className={styles.note}>{labels.noMatch}</p>
            )}
            <div className={styles.groups}>
              {ordered.map(([key, g]) => {
                const all = total(key).map((o) => o.id);
                const count = all.filter((id) => chosen.has(id)).length;
                const complete = count === all.length;
                return (
                  <section key={key} className={styles.group} aria-label={g.label || undefined}>
                    {g.label && (
                      <div className={styles.head}>
                        <span className={styles.groupLabel}>{g.label}</span>
                        <span className={styles.count}>{labels.countOf(count, all.length)}</span>
                        {!needle && (
                          <button
                            type="button"
                            className={styles.all}
                            onClick={() => toggle(all, !complete)}
                            aria-label={`${complete ? labels.selectNone : labels.selectAll}: ${g.label}`}
                          >
                            {complete ? labels.selectNone : labels.selectAll}
                          </button>
                        )}
                      </div>
                    )}
                    <ul className={styles.list}>
                      {g.options.map((o) => {
                        const on = chosen.has(o.id);
                        return (
                          <li key={o.id}>
                            <button
                              type="button"
                              role="checkbox"
                              aria-checked={on}
                              className={styles.option}
                              onClick={() => toggle([o.id], !on)}
                            >
                              <span className={styles.optionLabel}>{o.label}</span>
                              {on && <Icon name="check" size="sm" className={styles.check} />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          </Dialog>,
          document.body,
        )}
    </>
  );
}
